// OGSRig2Mesh — the DISTILLED rig→vertices node (controls[1,D] → vertices[1,V,3]).
//
// THE DEFAULT rig, for now. The exact MetaHuman rig (OrlDeform: OpenRigLogic +
// the character's own DNA, ONE asset for a whole head against ~74 MB per branch
// here) is opt-in behind `?rig=orl` while it is proven on real characters, so
// every existing character keeps driving through this model. `auto` reaches for
// the exact rig only when a bundle declares no rig2mesh this app can load — where
// the alternative is a frozen head rather than this. Selection lives in
// src/ogs/inference/rigMode.ts; OGSBranch.makeDeform maps it onto one branch.
//
// It satisfies OGSBranch's `BranchDeform` unchanged: this file is exactly what it
// was before the exact rig landed, because the interface was drawn around both.
//
// The whole point is the gpu-buffer contract: on the shared-device path ORT
// leaves `vertices` in GPU memory and GpuLifter.setPoseGpu copies it straight
// into the lifter's vert buffer (copyBufferToBuffer → vert transform → jacobians,
// ONE submission, no readback). ORT owns that buffer until the next run(), so
// the consumer MUST consume it in the same pipeline cycle — safe because the
// global withOrtGate means no two ORT ops overlap, so the prior frame's tensor
// is provably drained before the next run disposes it.
//
// runCpu() is the deliberate, init-only exception: calibration (corr = neutral −
// rig2mesh(baseRig)) and the single-region mean-offset learning need the verts
// on the CPU exactly once per branch, at boot.

import { DecoderSession } from "../DecoderSession.ts";
import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";

export interface OGSRig2MeshOptions {
  id: string;
  rigInputPort?: string; // graph-facing input port (default 'rig') → ORT 'controls'
  outputPort?: string; // graph-facing output port (default 'verts') ← ORT 'vertices'
  providers?: string[];
  bytes?: string | Uint8Array; // runtime path: the rig2mesh ONNX
  extra?: Record<string, unknown>; // profiler session options
  session?: DecoderSession; // test path: inject one
}

export class OGSRig2Mesh implements Node {
  readonly id: string;
  readonly type = "ogsRig2Mesh";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings = [];

  private readonly rigPort: string;
  private readonly outPort: string;
  private readonly providers: string[];
  private readonly bytes?: string | Uint8Array;
  private readonly extra: Record<string, unknown>;
  private session: DecoderSession | null;
  private rigDim = 0;

  constructor(opts: OGSRig2MeshOptions) {
    this.id = opts.id;
    this.rigPort = opts.rigInputPort ?? "rig";
    this.outPort = opts.outputPort ?? "verts";
    this.inputs = [{ name: this.rigPort }];
    this.outputs = [{ name: this.outPort }];
    this.providers = opts.providers ?? [];
    this.bytes = opts.bytes;
    this.extra = opts.extra ?? {};
    this.session = opts.session ?? null;
  }

  async init(ctx: NodeContext): Promise<void> {
    this.rigDim = ctx.rigDim;
    if (this.session) return;
    if (!this.bytes) throw new Error(`OGSRig2Mesh "${this.id}": no model bytes (and no injected session)`);
    const gpuIo = ctx.gpuBufferIo;
    this.session = await DecoderSession.create(
      ctx.ort as never,
      this.bytes,
      this.providers,
      {
        inputs: [{ port: this.rigPort, ortName: "controls" }],
        // Some exported rig2mesh graphs name their sole output differently —
        // accept it (warned once) rather than failing the whole pose path.
        outputs: [{ port: this.outPort, ortName: "vertices", gpuBuffer: gpuIo, soleOutputFallback: true }],
      },
      // ORT silently ignores the hint on the WASM fallback, so the consumer's
      // `loc` check still gates the CPU path.
      gpuIo ? { ...this.extra, preferredOutputLocation: { vertices: "gpu-buffer" } } : this.extra,
    );
  }

  async evaluate(
    _frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    if (!this.session) throw new Error(`OGSRig2Mesh "${this.id}": no session (init not run?)`);
    const rig = inbound[this.rigPort];
    if (!rig) throw new Error(`OGSRig2Mesh "${this.id}": missing input "${this.rigPort}"`);
    return this.session.run({ [this.rigPort]: rig });
  }

  /** Convenience: run from a raw rig vector (the pipeline's per-rig driver). */
  async pose(rig: Float32Array): Promise<NodeTensor> {
    const out = await this.evaluate({} as FrameInputs, {
      [this.rigPort]: { loc: "cpu", data: rig, dims: [1, this.rigDim || rig.length] },
    });
    return out[this.outPort];
  }

  /** Init-only CPU readback: the verts as a Float32Array, downloading them when
   *  they landed on a gpu-buffer. Used ONCE per branch to learn the anchor
   *  correction — never on the per-rig path. */
  async runCpu(rig: Float32Array): Promise<Float32Array> {
    return this.download(await this.pose(rig));
  }

  /** A verts tensor as a Float32Array, downloading a gpu-buffer one. Off the
   *  hot path by construction — calibration, or the loud CPU pose fallback. */
  async download(t: NodeTensor): Promise<Float32Array> {
    if (t.loc === "cpu") return t.data;
    // getData(false): the DecoderSession still owns this tensor and disposes it
    // on the next run — releasing it here would double-free.
    return (await (t.tensor as unknown as { getData: (release?: boolean) => Promise<unknown> }).getData(
      false,
    )) as Float32Array;
  }

  /** The live session — the gpu-buffer DEMOTE path + the ORT profile dump. */
  get activeSession(): DecoderSession | null {
    return this.session;
  }

  dirtyOn(): readonly DirtySource[] {
    return ["rig"];
  }

  dispose(): void {
    try { (this.session as { dispose?: () => void } | null)?.dispose?.(); } catch { /* best-effort */ }
    this.session = null;
  }
}
